import * as React from "react";
import { cn } from "@/lib/utils";

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /**
   * Size variant of the textarea
   * @default "default"
   */
  textareaSize?: "sm" | "default" | "lg";
  /**
   * Whether the textarea has an error state
   */
  error?: boolean;
  /**
   * Whether the textarea is resizable
   * @default "vertical"
   */
  resize?: "none" | "vertical" | "horizontal" | "both";
}

/**
 * Textarea component with consistent styling from the design system.
 * Supports different sizes, error states, and resize options.
 *
 * @example
 * <Textarea placeholder="Enter description" />
 * <Textarea textareaSize="sm" rows={3} />
 * <Textarea error placeholder="Invalid input" />
 * <Textarea resize="none" />
 */
const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, textareaSize = "default", error, resize = "vertical", ...props }, ref) => {
    return (
      <textarea
        className={cn(
          // Base shadcn/ui styles
          "flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background",
          "placeholder:text-muted-foreground",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          // Size variants
          textareaSize === "sm" && "min-h-[60px] text-xs",
          textareaSize === "default" && "min-h-[80px]",
          textareaSize === "lg" && "min-h-[100px] text-base",
          // Resize behavior
          resize === "none" && "resize-none",
          resize === "vertical" && "resize-y",
          resize === "horizontal" && "resize-x",
          resize === "both" && "resize",
          // Error state
          error && "border-destructive focus-visible:ring-destructive",
          className,
        )}
        ref={ref}
        aria-invalid={error || undefined}
        {...props}
      />
    );
  },
);

Textarea.displayName = "Textarea";

export { Textarea };
